import { useState } from 'react';
import { ChevronDown, ChevronUp, Shield } from 'lucide-react';
import type { SavedQuery } from '../../../types/query';
import SwaggerTryItPanel from './SwaggerTryItPanel';

interface SwaggerEndpointSectionProps {
  query: SavedQuery;
}

export default function SwaggerEndpointSection({ query }: SwaggerEndpointSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<'schema' | 'tryit'>('tryit');

  const columnsToShow = query.selected_columns.length > 0
    ? query.selected_columns
    : [];

  const path = `/api/services/${query.endpoint_slug}`;

  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-blue-50 hover:bg-blue-100 transition-colors"
      >
        <span className="px-2.5 py-1 bg-blue-600 text-white text-xs font-bold rounded uppercase tracking-wider">
          GET
        </span>
        <span className="flex-1 text-left text-sm font-mono text-gray-800">{path}</span>
        <span className="text-sm text-gray-500 hidden sm:inline">{query.name}</span>
        {isExpanded
          ? <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        }
      </button>

      {isExpanded && (
        <div className="p-5 space-y-5 bg-white">
          {(query.documentation_description || query.description) && (
            <p className="text-sm text-gray-600 leading-relaxed">
              {query.documentation_description || query.description}
            </p>
          )}

          <div className="flex gap-1 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab('tryit')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === 'tryit'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Try it out
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('schema')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === 'schema'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Schema
            </button>
          </div>

          {activeTab === 'tryit' && <SwaggerTryItPanel query={query} />}

          {activeTab === 'schema' && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" />
                  Security
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-12 gap-0 bg-gray-50 border-b border-gray-200 px-4 py-2">
                    <div className="col-span-3 text-xs font-semibold text-gray-500 uppercase">Nome</div>
                    <div className="col-span-3 text-xs font-semibold text-gray-500 uppercase">Tipo</div>
                    <div className="col-span-2 text-xs font-semibold text-gray-500 uppercase">In</div>
                    <div className="col-span-4 text-xs font-semibold text-gray-500 uppercase">Descricao</div>
                  </div>
                  <div className="grid grid-cols-12 gap-0 px-4 py-2.5 border-b border-gray-100">
                    <div className="col-span-3 text-sm font-mono text-gray-800">Authorization</div>
                    <div className="col-span-3">
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-mono">Bearer</span>
                    </div>
                    <div className="col-span-2 text-sm text-gray-500">header</div>
                    <div className="col-span-4 text-sm text-gray-500">Token de acesso (access_token)</div>
                  </div>
                  <div className="grid grid-cols-12 gap-0 px-4 py-2.5">
                    <div className="col-span-3 text-sm font-mono text-gray-800">personal-key</div>
                    <div className="col-span-3">
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-mono">apiKey</span>
                    </div>
                    <div className="col-span-2 text-sm text-gray-500">header</div>
                    <div className="col-span-4 text-sm text-gray-500">Chave pessoal do usuario</div>
                  </div>
                </div>
              </div>

              {query.filter_columns.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Parameters</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="grid grid-cols-12 gap-0 bg-gray-50 border-b border-gray-200 px-4 py-2">
                      <div className="col-span-3 text-xs font-semibold text-gray-500 uppercase">Nome</div>
                      <div className="col-span-3 text-xs font-semibold text-gray-500 uppercase">Tipo</div>
                      <div className="col-span-2 text-xs font-semibold text-gray-500 uppercase">In</div>
                      <div className="col-span-4 text-xs font-semibold text-gray-500 uppercase">Descricao</div>
                    </div>
                    {query.filter_columns.map(col => (
                      <div key={col} className="grid grid-cols-12 gap-0 px-4 py-2.5 border-b border-gray-100 last:border-0">
                        <div className="col-span-3 text-sm font-mono text-gray-800">{col}</div>
                        <div className="col-span-3">
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded font-mono">string</span>
                        </div>
                        <div className="col-span-2 text-sm text-gray-500">query</div>
                        <div className="col-span-4 text-sm text-gray-500">Filtrar por {col}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Response - 200 OK</h4>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm font-mono text-green-400 leading-relaxed">
{`{
  "success": true,
  "data": [
    {${columnsToShow.length > 0 ? '\n' + columnsToShow.map(col => `      "${col}": "value"`).join(',\n') + '\n    ' : '...'}}
  ],
  "metadata": {
    "total": 100,
    "timestamp": "${new Date().toISOString()}"
  }
}`}
                  </pre>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Response - 400</h4>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <pre className="text-sm font-mono text-amber-400">
{`{
  "success": false,
  "error": "Requisicao invalida"
}`}
                    </pre>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Response - 500</h4>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <pre className="text-sm font-mono text-red-400">
{`{
  "success": false,
  "error": "Erro interno"
}`}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
